import React, {useEffect, useState} from "react";
import {
    Box,
    Fab,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";
import {useDispatch, useSelector} from "react-redux";
import {AppState} from "../../store";
import {
    downloadContractFile,
    getContractAction,
    getContractFolderFilesAndFoldersAction,
    moveContractFileToFolderAction
} from "../../api/contract";
import AddIcon from "@mui/icons-material/Add";
import {AddFileToContractDialog} from "../add-file-to-contract/add-file-to-contract";
import {useLocation, useNavigate, useSearchParams} from "react-router-dom";
import File from "mdi-material-ui/File";
import Folder from "mdi-material-ui/Folder";
import {AddFolderToContractDialog} from "../add-folder-to-contract/add-folder-to-contract";
import {CreateNewFolder} from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FolderAction from "../../actions/FolderAction";
import {DeleteContractFolderDialog} from "../delete-folder/deleteContractFolderDialog";
import {CaseFileInfoDTO, CaseFolderDTO, UserPermission} from "../../api/dtos";
import {DragHandler} from "../legal-case-details/drag-handler";
import {setContract, setFolderFilesAndFolders, setLoadContractStatus} from "../../features/contracts/contractSlice";
import {DeleteContractFileDialog} from "../delete-file/deleteContractFileDialog";
import FileAction from "../../actions/FileAction";
import {adminOrHasPermission} from "../../shared/utils";

const handleDownloadContractFile = (file: CaseFolderDTO, contractId: number, dispatch: any, e: React.MouseEvent) => {
    e.preventDefault();
    dispatch(downloadContractFile({contractId, file}));
};

export interface ContractFilesProps {
    id: number;
}

interface DocumentRowProps {
    file: CaseFolderDTO;
    contractId: number;
}

const DocumentRow: React.FC<DocumentRowProps> = ({contractId, file}) => {
    const dispatch: any = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const [dragging, setDragging] = useState(false);
    const appendedNode = document.body;

    const handleDragStart = () => {
        setDragging(true);
    };

    const handleMouseUp = async (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        const dropAreaElement = target.closest(".drop-area");
        if (dropAreaElement && dropAreaElement.hasAttribute("data-id") !== undefined) {
            const dataId = dropAreaElement.getAttribute("data-id");
            const folderId = dataId == null ? null : Number(dataId);

            const result = await dispatch(moveContractFileToFolderAction({
                contractId,
                folderId,
                fileId: file.id
            }));

            if (result.type === "contract/moveContractFileToFolder/fulfilled") {
                const params = new URLSearchParams(location.search);
                if (folderId) {
                    params.set("folderId", folderId.toString());
                } else {
                    params.delete("folderId");
                }

                navigate(`?${params.toString()}`);
            }
        }
        setDragging(false);
    };

    const innerDownloadContractFile = (e: React.MouseEvent) => handleDownloadContractFile(file, contractId, dispatch, e)

    return (
        <DragHandler
            dragging={dragging}
            active={true}
            onDragStart={handleDragStart}
            onMouseUp={handleMouseUp}
            appendedNode={appendedNode}>
            <div style={{display: "flex", alignItems: "center"}}>
                <span className="document"><File/></span>
                <a
                    href="#"
                    onClick={innerDownloadContractFile}
                    style={{textDecoration: 'none', color: '#26a3ff'}}>
                    {file.name}
                </a>
            </div>
        </DragHandler>
    )
}

interface FolderInfo {
    folderId: number;
    folderName: string;
}

interface FolderRowProps {
    folderInfo: FolderInfo;
    contractId: number;
}

const FolderRow: React.FC<FolderRowProps> = ({folderInfo, contractId}) => {
    const navigate = useNavigate();
    const dispatch: any = useDispatch();
    const goFolder = () => {
        dispatch(setFolderFilesAndFolders(null));
        navigate(`/sozlesmeler/${contractId}/dosyalar?folderId=${folderInfo.folderId}`);
    }

    return (
        <div className="drop-area" data-id={folderInfo.folderId}>
            <div style={{display: "flex", alignItems: "center"}}>
                <span className="folder"><Folder/></span>
                <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        goFolder();
                    }}
                    style={{textDecoration: 'none', color: '#26a3ff'}}>
                    {folderInfo.folderName}
                </a>
            </div>
        </div>
    )
}

export const ContractFiles: React.FC<ContractFilesProps> = ({id}) => {
    const location = useLocation();
    const dispatch: any = useDispatch();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const folderId = searchParams.get('folderId') ? Number(searchParams.get('folderId')) : undefined;
    const user = useSelector((state: AppState) => state.auth.userWithToken?.user);
    const hasContractFileCreatePermission = adminOrHasPermission(user, UserPermission.CONTRACT_FILE_ADD);
    const contract = useSelector((state: AppState) => state.contract.contract);
    const folderFilesAndFolders = useSelector((state: AppState) => state.contract.folderFilesAndFolders);
    const [addFileDialogOpen, setAddFileDialogOpen] = useState(false);
    const [addFolderDialogOpen, setAddFolderDialogOpen] = useState(false);
    const [deleteFileDialogOpen, setDeleteFileDialogOpen] = useState(false);
    const [activeFile, setActiveFile] = useState<CaseFileInfoDTO | null>(null);
    const [deleteFolderDialogOpen, setDeleteFolderDialogOpen] = useState(false);
    const [activeFolder, setActiveFolder] = useState<CaseFolderDTO | null>(null);

    const handleAddFileDialogOpen = () => setAddFileDialogOpen(true);
    const handleAddFileDialogClose = () => setAddFileDialogOpen(false);

    const handleAddFolderDialogOpen = () => setAddFolderDialogOpen(true);
    const handleAddFolderDialogClose = () => setAddFolderDialogOpen(false);

    useEffect(() => {
        dispatch(getContractAction(id));
        dispatch(getContractFolderFilesAndFoldersAction({contractId: id, folderId}));

        return () => {
            dispatch(setContract(null));
            dispatch(setLoadContractStatus(null));
            dispatch(setFolderFilesAndFolders(null));
        };
    }, [id, dispatch]);

    useEffect(() => {
        dispatch(getContractFolderFilesAndFoldersAction({contractId: id, folderId}));
    }, [folderId]);

    const handleFileAdd = () => dispatch(getContractFolderFilesAndFoldersAction({contractId: id, folderId}));
    const handleFolderAdd = () => dispatch(getContractFolderFilesAndFoldersAction({contractId: id, folderId}));

    const openAddFile = () => handleAddFileDialogOpen();

    const openAddFolder = () => handleAddFolderDialogOpen();

    const openDelete = (folder: CaseFolderDTO) => handleDeleteFolderDialogOpen(folder);

    const openDeleteFile = (file: CaseFileInfoDTO) => handleDeleteFileDialogOpen(file);

    if (!contract) {
        return null;
    }
    let folders = folderFilesAndFolders?.folders;
    let files = folderFilesAndFolders?.files;
    const folder = folderFilesAndFolders?.folder;

    files = files?.filter((it) => it.folder?.id === folderId);
    folders = folders?.filter((it) => it.parentFolderId === folderId)

    const goBack = () => {
        const searchParams = new URLSearchParams(location.search);
        if (folder?.parentFolderId) {
            searchParams.set('folderId', folder.parentFolderId.toString());
        } else {
            searchParams.delete('folderId');
        }
        navigate(`?${searchParams.toString()}`);
    }

    const handleDeleteFolderDialogOpen = (folder: CaseFolderDTO) => {
        setActiveFolder(folder);
        setDeleteFolderDialogOpen(true);
    };

    const handleDeleteFolderDialogClose = () => {
        setActiveFolder(null);
        setDeleteFolderDialogOpen(false);
    };

    const handleFolderDelete = () => {
        dispatch(getContractFolderFilesAndFoldersAction({contractId: id, folderId}));
        handleDeleteFolderDialogClose();
    };

    const handleDeleteFileDialogOpen = (file: CaseFileInfoDTO) => {
        setActiveFile(file);
        setDeleteFileDialogOpen(true);
    };

    const handleDeleteFileDialogClose = () => {
        setActiveFile(null);
        setDeleteFileDialogOpen(false);
    };

    const handleFileDelete = () => {
        dispatch(getContractFolderFilesAndFoldersAction({contractId: id, folderId}));
        handleDeleteFileDialogClose();
    };

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    p: 1,
                    m: 1,
                    borderRadius: 1,
                }}>
                {folder && (
                    <div className="drop-area" data-id={folder.parentFolderId || null}
                         style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
                        <Fab
                            size="small"
                            color="default"
                            aria-label="back"
                            onClick={goBack}>
                            <ArrowBackIcon/>
                        </Fab>

                        <Typography variant="body1" sx={{ml: 5}}>
                            <strong>{folder.name}</strong>
                        </Typography>
                    </div>
                )}
                {hasContractFileCreatePermission && (
                    <>
                        <Fab
                            size="small"
                            color="primary"
                            aria-label="add"
                            sx={{marginLeft: "auto", marginBottom: 7}}
                            onClick={openAddFile}>
                            <AddIcon/>
                        </Fab>
                        <Fab
                            size="small"
                            color="primary"
                            aria-label="add"
                            sx={{marginBottom: 7, marginLeft: 2}}
                            onClick={openAddFolder}>
                            <CreateNewFolder/>
                        </Fab>
                    </>
                )}
            </Box>

            {addFileDialogOpen && (
                <AddFileToContractDialog
                    contract={contract}
                    folderId={folderId}
                    onClose={handleAddFileDialogClose}
                    onFileAdded={handleFileAdd}/>
            )}

            {addFolderDialogOpen && (
                <AddFolderToContractDialog
                    contract={contract}
                    folderId={folderId}
                    onClose={handleAddFolderDialogClose}
                    onFolderAdded={handleFolderAdd}/>
            )}

            {deleteFolderDialogOpen && activeFolder && (
                <DeleteContractFolderDialog
                    handleClose={handleDeleteFolderDialogClose}
                    handleDelete={handleFolderDelete}
                    contractId={id}
                    folderId={activeFolder.id}
                    folderName={activeFolder.name}/>
            )}

            {deleteFileDialogOpen && activeFile && (
                <DeleteContractFileDialog
                    handleClose={handleDeleteFileDialogClose}
                    handleDelete={handleFileDelete}
                    contractId={id}
                    fileId={activeFile.id}
                    fileName={activeFile.name}/>
            )}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell style={{width: "45%"}}>Dosya Adı</TableCell>
                            <TableCell style={{width: "45%"}}>Açıklama</TableCell>
                            <TableCell style={{width: "10%"}}>İşlemler</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {folders?.map((it) => (
                            <TableRow key={it.id}>
                                <TableCell>
                                    <FolderRow contractId={contract.id}
                                               folderInfo={{folderId: it.id, folderName: it.name}}/>
                                </TableCell>
                                <TableCell/>
                                <TableCell>
                                    <FolderAction
                                        folder={it}
                                        onDelete={openDelete}/>
                                </TableCell>
                            </TableRow>
                        ))}
                        {files?.map((it) => (
                            <TableRow key={it.id}>
                                <TableCell>
                                    <DocumentRow contractId={contract.id} file={it}/>
                                </TableCell>
                                <TableCell>{it.description}</TableCell>
                                <TableCell>
                                    <FileAction file={it} onDelete={openDeleteFile}/>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};